import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

// Evento customizado para sincronizar avatar entre componentes
const AVATAR_UPDATE_EVENT = 'avatar-updated';

export const useUserAvatar = () => {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { user } = useAuth();

  // Buscar avatar do perfil
  useEffect(() => {
    const fetchAvatar = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }

      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('avatar_url')
          .eq('id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Erro ao buscar avatar:', error);
        } else if (profile?.avatar_url) {
          setAvatarUrl(profile.avatar_url);
        } else {
          setAvatarUrl(null);
        }
      } catch (error) {
        console.error('Erro ao buscar avatar:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAvatar();

    // Escutar eventos de atualização de avatar
    const handleAvatarUpdate = (event: CustomEvent<string | null>) => {
      setAvatarUrl(event.detail);
    };

    window.addEventListener(AVATAR_UPDATE_EVENT as any, handleAvatarUpdate as EventListener);

    return () => {
      window.removeEventListener(AVATAR_UPDATE_EVENT as any, handleAvatarUpdate as EventListener);
    };
  }, [user?.id]);

  // Função para fazer upload do avatar
  const uploadAvatar = async (file: File): Promise<string | null> => {
    if (!user?.id) {
      toast({
        title: 'Erro',
        description: 'Usuário não encontrado',
        variant: 'destructive',
      });
      return null;
    }

    try {
      setIsUploading(true);

      // Validar tipo de arquivo
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Erro',
          description: 'Por favor, selecione um arquivo de imagem',
          variant: 'destructive',
        });
        return null;
      }

      // Validar tamanho (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Erro',
          description: 'O arquivo deve ter no máximo 5MB',
          variant: 'destructive',
        });
        return null;
      }

      // Criar nome único para o arquivo
      const fileExt = file.name.split('.').pop() || 'jpg';
      const fileName = `avatar-${Date.now()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      // Remover avatar antigo se existir
      if (avatarUrl) {
        try {
          // Tentar extrair o caminho do arquivo da URL
          const urlParts = avatarUrl.split('/');
          const bucketIndex = urlParts.findIndex(part => part === 'avatars');
          if (bucketIndex !== -1) {
            const oldPath = urlParts.slice(bucketIndex + 1).join('/');
            await supabase.storage
              .from('avatars')
              .remove([oldPath]);
          }
        } catch (error) {
          console.warn('Erro ao remover avatar antigo:', error);
          // Continuar mesmo se não conseguir remover
        }
      }

      // Fazer upload do novo avatar
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          upsert: true,
          cacheControl: '3600',
        });

      if (uploadError) {
        throw uploadError;
      }

      // Obter URL pública
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      if (!publicUrl) {
        throw new Error('URL pública não gerada');
      }

      // Atualizar perfil com nova URL do avatar
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ 
          avatar_url: publicUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (updateError) {
        throw updateError;
      }

      // Atualizar estado local
      setAvatarUrl(publicUrl);

      // Disparar evento para atualizar todos os componentes que usam o hook
      window.dispatchEvent(new CustomEvent(AVATAR_UPDATE_EVENT, { detail: publicUrl }));

      toast({
        title: 'Avatar atualizado!',
        description: 'Sua foto de perfil foi atualizada com sucesso.',
      });

      return publicUrl;
    } catch (error: any) {
      console.error('Erro ao fazer upload do avatar:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Não foi possível fazer upload do avatar. Tente novamente.',
        variant: 'destructive',
      });
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    avatarUrl,
    isLoading,
    isUploading,
    uploadAvatar,
    refreshAvatar: async () => {
      if (!user?.id) return;
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('avatar_url')
          .eq('id', user.id)
          .maybeSingle();
        
        const newAvatarUrl = profile?.avatar_url || null;
        setAvatarUrl(newAvatarUrl);
        
        // Disparar evento para atualizar todos os componentes que usam o hook
        window.dispatchEvent(new CustomEvent(AVATAR_UPDATE_EVENT, { detail: newAvatarUrl }));
      } catch (error) {
        console.error('Erro ao atualizar avatar:', error);
      }
    }
  };
};
